import React, { useEffect } from "react";
import dayjs from "dayjs";
import router from "next/router";

import {
  BookingCardStatusContainer,
  BadgeContainer,
  TicketHolder,
  TicketHolderContainer,
  BookingCardContainer,
  BookingCardContentContainer,
  UserStatusContainer,
  ButtonsContainer,
  StatusWithActionContainer,
  BottomBarActions,
} from "./BookingCard.styles";
import { SessionCardBottomBar } from "../SessionCard/SessionCard.styles";
import {
  MeshGradient,
  MeshColours,
  SessionDetails,
  TicketIcon,
  Badge,
  generateSessionDetails,
  Countdown,
  TicketDetails,
  StatusTag,
  StatusTagVariants,
  Button,
  ManageBookingMenu,
  TicketVariantsType,
  RejoinQueueDialog,
  RejoinVariant,
  OfferCountdown,
} from "..";
import { SessionDetailsInterface } from "../../models/Session";
import { UserBookingInterest } from "../../models/BookingInterest";
import { joinLastMinQueue } from "../../services/BookingInterestV2Service";
import { Icon } from "../../icons";
import { Box } from "@mui/material";
import { useModal } from "src/contexts/ModalContext";
import { IBookingTicket, IUserBookingTicket } from "src/models/BookingTicket";
import { AllocationType } from "src/models/Allocation";

export const getTicketType = (ticketHolders: TicketDetails[], allocationType?: AllocationType | undefined,) => {
  let ticketType: TicketVariantsType = "generalAdmission";

  const hasPriority = ticketHolders?.some((element) => element.isPriorityTicket === true);
  const hasAccessibility = ticketHolders?.some((element) => element.hasAccessibilityRequirements === true);

  switch (allocationType) {
    case "FriendsAndFamily":
      ticketType = "friendsAndFamily"
      break;
    case "VIP":
      ticketType = "vip"
      break;
    case "Priority":
      ticketType = hasAccessibility ? "priorityAccessibility" : "priority";
      break;
    default:
      if (hasPriority && hasAccessibility) {
        ticketType = "priorityAccessibility";
      } else if (hasPriority) {
        ticketType = "priority";
      } else if (hasAccessibility) {
        ticketType = "accessibilityRequired";
      }
  }

  return ticketType;
}

export interface CustomBookingCardProps {
  bookingInterest: UserBookingInterest;
  colour?: MeshColours;
  isTicketGenerating?: boolean;
  handleAcceptTicket: (bookingInterest: UserBookingInterest) => Promise<void>;
  handleDeclineTicket: (bookingInterest: UserBookingInterest) => Promise<void>;
  handleDeclineAttendance: (bookingInterest: UserBookingInterest) => Promise<void>;
  handleDownloadTicket: (userEmail: string, ticketId: number) => Promise<void>;
  onModalAction?: (successful?: boolean, message?: React.ReactElement | string) => void;
}

export const BookingCard = ({
  bookingInterest,
  isTicketGenerating = false,
  handleAcceptTicket,
  handleDeclineTicket,
  handleDeclineAttendance,
  handleDownloadTicket,
  onModalAction,
  colour = "pink-purple-green",
  ...rest
}: CustomBookingCardProps) => {
  const { session, user, ticket, status } = bookingInterest;
  const { modalBookingData, setCurrentBookingData } = useModal();

  const allocationSession = bookingInterest.allocationSession;

  const [sessionDetails, setSessionDetails] =
    React.useState<SessionDetailsInterface>({
      name: session.name,
      date: "",
      arrivalTime: "",
      approxFinishTime: "",
      location: null,
    });
  const [isAcceptLoading, setAcceptLoading] = React.useState<boolean>(false)
  const [isDeclineLoading, setDeclineLoading] = React.useState<boolean>(false)
  const [isDeclineAttendanceLoading, setDeclineAttendanceLoading] = React.useState<boolean>(false)
  const [isDownloadLoading, setDownloadLoading] = React.useState<boolean>(false)
  const [isConfirmAttendanceLoading, setConfirmAttendanceLoading] = React.useState<boolean>(false)

  const [minAge, setMinAge] = React.useState<number | undefined>(0)

  // Consolidated flow (TTT-687): second chance rejoin entry point.
  const [rejoinDialogOpen, setRejoinDialogOpen] = React.useState<boolean>(false);
  const [isJoinLastMinLoading, setIsJoinLastMinLoading] = React.useState<boolean>(false);

  const handleDownloadTicketInternal = async (userEmail: string, ticketId: number) => {
    setDownloadLoading(true);
    await handleDownloadTicket(userEmail, ticketId)
    setDownloadLoading(false);
  }

  const handleAcceptTicketInternal = async (bookingInterest: UserBookingInterest) => {
    setAcceptLoading(true);
    await handleAcceptTicket(bookingInterest)

    setAcceptLoading(false);
  }

  const handleDeclineTicketInternal = async (bookingInterest: UserBookingInterest) => {
    setDeclineLoading(true);
    await handleDeclineTicket(bookingInterest)
    setDeclineLoading(false);
  }

  const handleDeclineAttendanceInternal = async (bookingInterest: UserBookingInterest) => {
    setDeclineAttendanceLoading(true);
    await handleDeclineAttendance(bookingInterest)
    setDeclineAttendanceLoading(false);
  }

  const handleConfirmAttendanceInternal = async (bookingInterest: UserBookingInterest) => {
    setConfirmAttendanceLoading(true);
    router.push({
      pathname: "/attendance",
      query: { bookingInterestId: bookingInterest.id },
    })
    setConfirmAttendanceLoading(false);
  }

  const fillBookingData = (bookingInterestToFillWith: UserBookingInterest) => {
    return {
      id: bookingInterestToFillWith.id,
      loggedInUserTicket: {
        id: bookingInterestToFillWith.user.id,
        firstName: bookingInterestToFillWith.assignedFirstName ?? bookingInterestToFillWith.user.firstName,
        lastName: bookingInterestToFillWith.assignedLastName ?? bookingInterestToFillWith.user.lastName,
        email: bookingInterestToFillWith.user.email,
        age: bookingInterestToFillWith.userAgeAtBooking ?? bookingInterestToFillWith.user.age,
        accessibility: bookingInterestToFillWith.accessibility ?? bookingInterestToFillWith.user.accessibility ?? "None",
        otherAccessibility: bookingInterestToFillWith.otherAccessibility ?? bookingInterestToFillWith.user.otherAccessibility ?? "",
        status: bookingInterestToFillWith.status,
        parentId: bookingInterestToFillWith.parentId
      } as IBookingTicket,
      ticketForAnotherPerson: bookingInterestToFillWith.relatedBookingInterests?.map(item => {
        return {
          bookingInterestId: item.id,
          id: item.user.id,
          firstName: item.assignedFirstName ?? item.user.firstName,
          lastName: item.assignedLastName ?? item.user.lastName,
          email: item.user.email,
          age: item.userAgeAtBooking ?? item.user.age,
          accessibility: item.accessibility ?? item.user.accessibility,
          otherAccessibility: item.otherAccessibility ?? item.user.otherAccessibility,
          status: item.status,
        }
      })
    } as IUserBookingTicket;
  }

  const setBookingData = () => {
    setCurrentBookingData({
      minAge: minAge,
      bookingInterest: fillBookingData(bookingInterest),
      isPriority: allocationSession?.allocation?.type === "Priority"
    })
  }

  useEffect(() => {
    if (bookingInterest.id == modalBookingData.bookingInterest.id) {
      setCurrentBookingData({
        minAge: minAge,
        bookingInterest: fillBookingData(bookingInterest),
        isPriority: allocationSession?.allocation?.type === "Priority"
      })
    }

    if (session) {
      const sessionDetailsObj = generateSessionDetails(session);
      setSessionDetails(sessionDetailsObj);

      const showMinimumAge = session?.show?.defaultMinimumAgeRequirement;
      setMinAge(sessionDetailsObj?.minimumAgeRequirement ?? (showMinimumAge === null ? 0 : showMinimumAge))
    }
  }, [bookingInterest]);

  let ticketHolders = [
    {
      user: {
        firstName: user.firstName,
        lastName: user.lastName,
        email: user.email,
      },
      assignedFirstName: bookingInterest?.assignedFirstName ?? user.firstName,
      assignedLastName: bookingInterest?.assignedLastName ?? user.lastName,
      ticket: bookingInterest.ticket,
      isLeadBooker: bookingInterest.parentId === null,
      accessibility: bookingInterest.accessibility,
      otherAccessibility: bookingInterest.otherAccessibility,
      hasAccessibilityRequirements: bookingInterest.hasAccessibilityRequirements,
      accessibilityRequestStatus: (bookingInterest.accessibilityRequestStatus === "Accepted" && bookingInterest.hasAccessibilityRequirements) ? "Accepted" : "Pending",
      bookingInterestStatus: status,
      isChildBooking: false,
      isRegistered: user.isRegistered,
    },
  ] as TicketDetails[];

  bookingInterest.relatedBookingInterests && bookingInterest.relatedBookingInterests.length <= 5 && bookingInterest.relatedBookingInterests.map(
    (relatedBookingInterest: UserBookingInterest) => {
      ticketHolders.push({
        id: relatedBookingInterest.id!,
        user: {
          ...relatedBookingInterest.user,
          email: relatedBookingInterest.user.email || user.email
        },
        assignedFirstName: relatedBookingInterest.assignedFirstName ?? relatedBookingInterest.user.firstName,
        assignedLastName: relatedBookingInterest.assignedLastName ?? relatedBookingInterest.user.lastName,
        ticket: relatedBookingInterest.ticket,
        isLeadBooker: false,
        isChildBooking: true,
        userAgeAtBooking: relatedBookingInterest.userAgeAtBooking ?? relatedBookingInterest.user.age,
        accessibility: relatedBookingInterest.accessibility,
        otherAccessibility: relatedBookingInterest.otherAccessibility,
        hasAccessibilityRequirements: relatedBookingInterest.hasAccessibilityRequirements,
        accessibilityRequestStatus: relatedBookingInterest.accessibilityRequestStatus,
        bookingInterestStatus: relatedBookingInterest.status,
        isDependent: relatedBookingInterest.isDependent,
        isRegistered: relatedBookingInterest.user.isRegistered,
      });
    }
  );

  bookingInterest.parentAndSiblingInterests && bookingInterest.parentAndSiblingInterests.map(
    (parentAndSiblingInterest: UserBookingInterest) => {
      ticketHolders.push({
        id: parentAndSiblingInterest.id!,
        user: {
          ...parentAndSiblingInterest.user,
          email: parentAndSiblingInterest.user.email || user.email
        },
        assignedFirstName: parentAndSiblingInterest.assignedFirstName ?? parentAndSiblingInterest.user.firstName,
        assignedLastName: parentAndSiblingInterest.assignedLastName ?? parentAndSiblingInterest.user.lastName,
        isLeadBooker: parentAndSiblingInterest.parentId == null,
        isChildBooking: true, //parentAndSiblingInterest.parentId != null,
        userAgeAtBooking: parentAndSiblingInterest.userAgeAtBooking ?? parentAndSiblingInterest.user.age,
        accessibility: parentAndSiblingInterest.accessibility,
        otherAccessibility: parentAndSiblingInterest.otherAccessibility,
        hasAccessibilityRequirements: parentAndSiblingInterest.hasAccessibilityRequirements,
        accessibilityRequestStatus: parentAndSiblingInterest.accessibilityRequestStatus,
        bookingInterestStatus: parentAndSiblingInterest.status,
        isDependent: parentAndSiblingInterest.isDependent,
        isRegistered: parentAndSiblingInterest.user.isRegistered,
        isParentOrSibling: true
      });
    }
  );

  const showName = session?.show?.name;
  const sessionDate = dayjs(session?.arrivalTime!);
  const pastShowDeadline = dayjs(session?.approxFinishTime!).add(3, 'hour');

  let isSessionEditable: boolean = false;
  let statusTagVariant: StatusTagVariants = "applicationPending";

  const currentDate = dayjs();

  const hasDependentBookingInterests = () => {
    const dependentBookingInterests = bookingInterest.relatedBookingInterests?.find(bI => bI?.user?.age && bI?.user?.age < 18)
    return !!dependentBookingInterests
  }

  const hasRelatedButNotRespondedBookingInterests = () => {
    const relatedButNotRespondedBookingInterest = bookingInterest.relatedBookingInterests?.find(bI => bI?.status &&
      bI.status === "InterestRegistered" || bI.status === "InvitedToTicket")
    return !!relatedButNotRespondedBookingInterest
  }

  if (pastShowDeadline.isBefore(currentDate)) {
    statusTagVariant = "pastShow";
  } else {
    switch (status) {
      case "InterestRegistered":
        isSessionEditable = true;
        statusTagVariant = "applicationPending";
        break;

      case "InvitedToTicket":
        isSessionEditable = true;
        statusTagVariant = "ticketOffer";
        break;

      case "TicketAccepted":
        isSessionEditable = hasDependentBookingInterests() || hasRelatedButNotRespondedBookingInterests();
        statusTagVariant = "tickets";
        break;

      case "TicketDeclined":
        isSessionEditable = false;
        statusTagVariant = "ticketOffer";
        break;

      case "TicketTimedOut":
        isSessionEditable = false;
        statusTagVariant = "offerExpired";
        break;

      // Consolidated flow (TTT-687/TTT-688)
      case "RejoinedQueue":
        isSessionEditable = false;
        statusTagVariant = "rejoinedQueue";
        break;

      case "JoinedLastMinQueue":
        isSessionEditable = false;
        statusTagVariant = "lastMinQueue";
        break;

      case "InterestDeclined":
        isSessionEditable = false;
        statusTagVariant = "interestDeclined";
        break;

      case "SecondChanceExpired":
        isSessionEditable = false;
        statusTagVariant = "secondChanceExpired";
        break;

      case "TurnedAway":
        isSessionEditable = false;
        statusTagVariant = "turnedAway";
        break;

      case "EnteredSession":
        isSessionEditable = false;
        statusTagVariant = "pastShow";
        break;

      case "UnattendedSession":
        isSessionEditable = false;
        statusTagVariant = "pastShow";
        break;

      case "AttendanceSMSReminderSent":
        isSessionEditable = true;
        statusTagVariant = "checkInPending";
        break;

      case "AttendanceConfirmedViaSMS":
        isSessionEditable = true;
        statusTagVariant = "checkedIn";
        break;

      case "AttendanceDeclinedViaSMS":
        isSessionEditable = false;
        statusTagVariant = "checkInDeclined";
        break;

      case "AttendanceConfirmationTimedOutViaSMS":
        isSessionEditable = false;
        statusTagVariant = "timedoutSms";
        break;

      case "TicketOrInterestRevoked":
        isSessionEditable = false;
        statusTagVariant = "ticketOrInterestRevoked";
        break;

      default:
        isSessionEditable = true;
        statusTagVariant = "applicationPending";
        break;
    }
  }

  // Consolidated flow (TTT-687): the rejoin CTA. The API is the authority on eligibility
  // (group rule, phone verification, cutoffs); this only hides the button in the cases the
  // client can already rule out.
  const rejoinVariant: RejoinVariant | null =
    status === "TicketTimedOut"
      ? "offerTimedOut"
      : status === "AttendanceConfirmationTimedOutViaSMS"
        ? "checkInMissed"
        : null;
  // The flow flag is part of the gate, not a detail: rejoining does not exist on legacy
  // sessions (RejoinQueueCommandHandler refuses outright), so without this the CTA would
  // show on any session with the flow switched off and then error on submit.
  const canRejoin =
    rejoinVariant !== null &&
    !!bookingInterest.session?.consolidatedFlowEnabled &&
    !bookingInterest.hasUsedSecondChance &&
    !bookingInterest.isDependent &&
    statusTagVariant !== "pastShow";

  /**
   * Consolidated flow: joining the last min list from the profile, for a booking still
   * waiting. The activation email's CTA does the same thing, and this is for everyone who
   * lost that email, never got it, or would simply rather do it here.
   *
   * Eligibility is the API's word (`canJoinLastMinQueue`) — the phase times it depends on are
   * not on this model, and a button that appears only to be refused is worse than none.
   */
  const canJoinLastMin = !!bookingInterest.canJoinLastMinQueue && statusTagVariant !== "pastShow";

  /**
   * The guest line's counterpart to the Join button.
   *
   * Joining moves this booking and any dependents on it, and nobody else
   * (JoinLastMinQueueCommandHandler, TTT-688 §6.2b): an adult invitee received the activation
   * email in their own right and answers from their own account. A card that showed only one
   * Join button under a list of names read as if it spoke for all of them, so each adult
   * guest's line says where their invitation actually went - while the lead can still join,
   * and after they have, which is when the question "what about them?" really lands.
   */
  const showLastMinGuestNote =
    (canJoinLastMin || status === "JoinedLastMinQueue") && statusTagVariant !== "pastShow";


  const handleJoinLastMinQueue = async () => {
    if (!bookingInterest.id) {
      return;
    }

    setIsJoinLastMinLoading(true);
    const result = await joinLastMinQueue(bookingInterest.id, false);
    setIsJoinLastMinLoading(false);

    onModalAction?.(
      result.success,
      result.success
        ? result.message ??
            "You're on the last minute list! Your place is based on when you originally applied."
        : result.error ?? "Unable to join the last minute list."
    );
  };

  // Sits on the lead booker's own ticket line, not in the card's bottom bar: from down there
  // it read as one action covering every name on the card, which is exactly what it is not.
  const renderJoinLastMinButton = () => (
    <Button
      data-cy="join-last-min-button"
      variant="primary"
      size="sm"
      loading={isJoinLastMinLoading}
      onClick={handleJoinLastMinQueue}
    >
      Join Last Min List
    </Button>
  );

  // Sized to its label, not `full`: it sits beside a status tag rather than sharing a pill
  // with a second button, so stretching it would push the tag out of the row.
  const renderRejoinButton = () => (
    <Button
      data-cy="rejoin-queue-button"
      variant="primary"
      size="sm"
      onClick={() => setRejoinDialogOpen(true)}
    >
      {rejoinVariant === "checkInMissed" ? "Join Last Min Queue" : "Rejoin Queue"}
    </Button>
  );

  const renderDownloadButton = (ticketHolder: TicketDetails) => {
    const ticketId = ticketHolder.ticket?.id;
    const ticketEmail = ticketHolder.user.email;
    const isTicketReady = Boolean(ticketId);
    const isGenerating = isTicketGenerating || !isTicketReady;

    return (
      <Button
        data-cy="download-ticket-button"
        leftIcon={isGenerating ? null : <Icon icon="Download" size="sm" />}
        variant="primary"
        size="sm"
        loading={isDownloadLoading || isGenerating}
        disabled={isGenerating}
        onClick={() => {
          if (isGenerating || !ticketId || !ticketEmail) {
            return;
          }
          handleDownloadTicketInternal(ticketEmail, ticketId);
        }}
        full
      >
        {isGenerating ? "Generating" : "Download"}
      </Button>
    );
  };

  const UserBookingStatusSwitch = (ticketHolder: TicketDetails) => {
    switch (ticketHolder.bookingInterestStatus) {
      // Consolidated flow: still waiting. The line says what this means for THIS person - the
      // lead booker (with any dependents riding on their ticket) gets the action once the last
      // min list opens, an adult guest gets told the invitation went to them directly. A parent
      // or sibling line belongs to somebody else's card and is shown here for context only, so
      // it stays silent.
      case "InterestRegistered":
      case "UserInterestConfirmedViaEmail":
        if (!ticketHolder.isChildBooking) {
          return canJoinLastMin ? renderJoinLastMinButton() : null;
        }
        // Once the show is behind us there is no application still pending — every other
        // branch of this switch checks the same thing before it claims a live state.
        if (ticketHolder.isDependent || ticketHolder.isParentOrSibling || statusTagVariant === "pastShow") {
          return null;
        }
        // An adult guest's line used to go blank outside the last-min window, so a lead booker
        // looking at their own card could not tell whether the guest was in the running, had
        // joined the list, or had never been asked. Something is always said now: where their
        // invitation went while the list is open, and otherwise the same "Application Pending"
        // the lead booker's own status tag shows.
        return showLastMinGuestNote
          ? <StatusTag variant="lastMinInvitedDirectly" size="sm" />
          : <StatusTag variant="applicationPending" size="sm" />;

      case "EnteredSession":
        return <StatusTag variant="attended" />;

      case "UnattendedSession":
        return <StatusTag variant="noShow" />;

      case "TurnedAway":
        return <StatusTag variant="turnedAway" />;

      case "TicketDeclined":
        return <StatusTag variant="declined" />;
      case "TicketOrInterestRevoked":
        return <StatusTag variant="ticketOrInterestRevoked" />;
      case "AttendanceDeclinedViaSMS":
        return <StatusTag variant="checkInDeclined" />;
      case "AttendanceConfirmationTimedOutViaSMS":
        // Consolidated flow: a missed check-in can rejoin the last min queue.
        if (canRejoin && !ticketHolder.isChildBooking) {
          return (
            <StatusWithActionContainer>
              <StatusTag variant="timedoutSms" />
              {renderRejoinButton()}
            </StatusWithActionContainer>
          );
        }
        return <StatusTag variant="timedoutSms" />;
      case "TicketTimedOut":
        // Consolidated flow: a timed-out offer can rejoin the queue.
        if (canRejoin && !ticketHolder.isChildBooking) {
          return (
            <StatusWithActionContainer>
              <StatusTag variant="timedout" />
              {renderRejoinButton()}
            </StatusWithActionContainer>
          );
        }
        return <StatusTag variant="timedout" />;
      case "RejoinedQueue":
        return <StatusTag variant="rejoinedQueue" />;
      case "JoinedLastMinQueue":
        return <StatusTag variant="lastMinQueue" />;
      case "InterestDeclined":
        return <StatusTag variant="interestDeclined" />;
      case "SecondChanceExpired":
        return <StatusTag variant="secondChanceExpired" />;
      case "TicketAccepted":
        if (statusTagVariant == "pastShow") {
          return <StatusTag variant="noShow" />;
        } else if (ticketHolder.isChildBooking) {
          return <StatusTag variant="ticketAccepted" />;
        } else {
          return (
            <ButtonsContainer>
              <Button
                variant="secondary"
                size="sm"
                loading={isDeclineLoading}
                onClick={() => handleDeclineTicketInternal(bookingInterest)}
                full
              >
                Cancel Ticket
              </Button>
              {renderDownloadButton(ticketHolder)}
            </ButtonsContainer>
          );
        }
      case "AttendanceConfirmedViaSMS":
        if (statusTagVariant == "pastShow") {
          return <StatusTag variant="noShow" />;
        } else if (ticketHolder.isChildBooking) {
          return <StatusTag variant="checkedIn" />;
        } else {
          return (
            <ButtonsContainer>
              <Button
                variant="secondary"
                data-cy="decline-attendance-button"
                size="sm"
                loading={isDeclineAttendanceLoading}
                onClick={() => handleDeclineAttendanceInternal(bookingInterest)}
                full
              >
                Cancel Ticket
              </Button>
              {renderDownloadButton(ticketHolder)}
            </ButtonsContainer>
          );
        }
      case "AttendanceSMSReminderSent":
        if (statusTagVariant == "pastShow") {
          return <StatusTag variant="noShow" />;
        } else if (ticketHolder.isChildBooking) {
          return <StatusTag variant="checkInPending" />;
        } else {
          return (
            <ButtonsContainer>
              <Button
                variant="secondary"
                data-cy="decline-attendance-button"
                size="sm"
                loading={isDeclineAttendanceLoading}
                onClick={() => handleDeclineAttendanceInternal(bookingInterest)}
                full
              >
                Cancel Ticket
              </Button>
              <Button
                sx={{ minWidth: "max-content" }}
                data-cy="confirm-attendance-button"
                variant="primary"
                size="sm"
                loading={isConfirmAttendanceLoading}
                onClick={() => handleConfirmAttendanceInternal(bookingInterest)}
                full
              >
                Confirm Attendance
              </Button>
            </ButtonsContainer>
          );
        }
      case "InvitedToTicket":
        if (statusTagVariant == "pastShow") {
          return <StatusTag variant="declined" />;
        } else if (ticketHolder.isChildBooking) {
          return <StatusTag variant="awaitingResponse" />;
        } else {
          return (
            <ButtonsContainer>
              <Button
                variant="secondary"
                size="sm"
                loading={isDeclineLoading}
                onClick={() => handleDeclineTicketInternal(bookingInterest)}
                full
              >
                Decline
              </Button>
              <Button
                data-cy="accept-ticket-button"
                variant="primary"
                size="sm"
                loading={isAcceptLoading}
                onClick={() => handleAcceptTicketInternal(bookingInterest)}
                full
              >
                Accept
              </Button>
            </ButtonsContainer>
          );
        }
      default:
        return null;
    }
  };

  const ticketType = getTicketType(ticketHolders, allocationSession?.allocation?.type);
  return (
    <BookingCardContainer {...rest} data-cy="bookingCard" data-id={ticket?.barcode}>
      <BookingCardStatusContainer>
        <StatusTag variant={statusTagVariant} />
        {/* Consolidated flow: how long is left on a live offer. Renders nothing when this
            booking is not waiting on a deadline, or once the show is behind us. */}
        {statusTagVariant !== "pastShow" && <OfferCountdown bookingInterest={bookingInterest} />}
      </BookingCardStatusContainer>

      <BookingCardContentContainer>
        <SessionDetails
          showName={showName}
          showMinimumAge={minAge}
          sessionDetails={sessionDetails}
          ticketType={ticketType}
          bookingInterest={bookingInterest}
          isPriority={ticketType === "priority" || ticketType === "priorityAccessibility"}
        />
        {ticketHolders &&
          ticketHolders.sort((holderA, holderB) => holderA.id - holderB.id).map((ticketHolder, index) => {
            // Rendered up front so the row can leave the status column out entirely when this
            // person has nothing to show - an empty flex box would still take its share of the
            // line and push the name around.
            const ticketHolderStatus = UserBookingStatusSwitch(ticketHolder);

            return (
              <TicketHolderContainer
                key={`ticket-holder-${bookingInterest.id}-${index}`}
                data-cy="bookingCard-ticketRow"
              >
                <TicketIcon
                  ticketNumber={index + 1}
                  ticketIconStyle={
                    statusTagVariant === "pastShow" ||
                      statusTagVariant === "tickets"
                      ? "outlined"
                      : (ticketType === "priority" || ticketType === "priorityAccessibility") ? "gold-outlined" : "dotted"
                  }
                />
                <TicketHolder data-cy="bookingCard-ticketHolder">
                  {ticketHolder.assignedFirstName} {ticketHolder.assignedLastName}
                </TicketHolder>
                <BadgeContainer>
                  {ticketHolder.isLeadBooker && (
                    <Badge badgeStyle="leadBooker" />
                  )}
                  {!!ticketHolder.userAgeAtBooking && ticketHolder.userAgeAtBooking < 18 && (
                    <Badge badgeStyle="underAge" />
                  )}
                  {/* Consolidated flow: a guest with no account of their own. A standing fact
                      about the booking, not a phase of it, so it shows whenever the card does
                      rather than only while the last min list is open — the lead booker is the
                      one who has to chase it and the sooner they know the better. Dependents are
                      exempt by definition: no email, no login, they ride on the lead's ticket. */}
                  {ticketHolder.isRegistered === false && !ticketHolder.isDependent && (
                    <Badge badgeStyle="noAccount" />
                  )}

                </BadgeContainer>
                {ticketHolder.hasAccessibilityRequirements && (
                  <Box>
                    <Badge badgeStyle="accessibilityRequired" size="sm" />
                  </Box>
                )}
                {ticketHolderStatus && (
                  <UserStatusContainer>
                    {ticketHolderStatus}
                  </UserStatusContainer>
                )}
              </TicketHolderContainer>
            );
          })}

        {bookingInterest.relatedBookingInterests && bookingInterest.relatedBookingInterests.length > 5 &&
          <TicketHolderContainer
            key={`ticket-holder-${bookingInterest.id}-${1}`}
          >
            <TicketIcon
              ticketNumber={1}
              ticketIconStyle={
                statusTagVariant === "pastShow" ||
                  statusTagVariant === "tickets"
                  ? "outlined"
                  : ticketType === "priority" ? "gold-outlined" : "dotted"
              }
            />
            <TicketHolder data-cy="bookingCard-ticketHolder">
              Group ticket with {bookingInterest.relatedBookingInterests.length} guests
            </TicketHolder>
            <BadgeContainer>
              <Badge badgeStyle="groupTicket" />
            </BadgeContainer>
            {bookingInterest.hasAccessibilityRequirements && (
              <Box>
                <Badge badgeStyle="accessibilityRequired" size="sm" />
              </Box>
            )}
          </TicketHolderContainer>
        }
        {isSessionEditable && (
          <SessionCardBottomBar>
            <Countdown dueDate={sessionDate} daysToGoWarning={2} isLiveCountDown={false} />
            <BottomBarActions>
              {/* Card-wide actions only. Joining the last min list is not one of them: it
                  moves the lead booker's line, so it is rendered on that line. */}
              <ManageBookingMenu
                setBookingData={setBookingData}
                disableEdit={!!bookingInterest.allocationSessionId && allocationSession?.allocation?.type !== "Priority"}
                onModalAction={onModalAction}
              />
            </BottomBarActions>
          </SessionCardBottomBar>
        )}
      </BookingCardContentContainer>
      {canRejoin && rejoinVariant && (
        <RejoinQueueDialog
          open={rejoinDialogOpen}
          variant={rejoinVariant}
          bookingInterest={bookingInterest}
          onClose={() => setRejoinDialogOpen(false)}
          onCompleted={onModalAction}
        />
      )}
      <MeshGradient
        colour={colour}
        position="bottom"
        size="low"
        sx={{
          top: ticketHolders && ticketHolders.length > 0 ? 0 : "auto",
          zIndex: 0,
        }}
      />
    </BookingCardContainer>
  );
};

export interface BookingCardProps
  extends React.ComponentProps<typeof BookingCard> {
  as?: React.ElementType;
}

BookingCard.displayName = "BookingCard";
